import {
  VentureContactTypeCreateDocument,
  VentureContactTypeDeleteDocument,
  VentureContactTypeGetByIdDocument,
  VentureContactTypeListDocument,
  VentureContactTypeUpdateDocument,
  VentureContactTypesPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";

import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";
import {
  IVentureContactTypeCreateVars,
  IVentureContactTypeModel,
  IVentureContactTypePaginatedResponse,
  IVentureContactTypeUpdateVars,
  IVentureContactTypesListVars,
  IVentureContactTypesPaginatedVars,
  VentureContactTypeCreateUpdateResponse,
  VentureContactTypesListResponse,
} from "./types";

export async function getVentureContactTypesPaginated(
  variables: IVentureContactTypesPaginatedVars
): Promise<IVentureContactTypePaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureContactTypesPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureContactTypesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureContactType(
  variables: IVentureContactTypeCreateVars
): Promise<VentureContactTypeCreateUpdateResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureContactTypeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureContactType",
      fieldNames: ["ventureContactTypesPaginated", "ventureContactTypeList"],
    });

    return res?.data?.ventureContactTypeCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureContactType(
  variables: IVentureContactTypeUpdateVars
): Promise<VentureContactTypeCreateUpdateResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureContactTypeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureContactType",
      id: variables.ventureContactTypeId.toString(),
      fieldNames: [
        "ventureContactTypesPaginated",
        "ventureContactTypeGetById",
        "ventureContactTypeList",
      ],
    });

    return res?.data?.ventureContactTypeUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getVentureContactType(
  ventureContactTypeId: number
): Promise<PartialNullable<IVentureContactTypeModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureContactTypeGetByIdDocument,
      variables: {
        ventureContactTypeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureContactTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureContactType(
  ventureContactTypeId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureContactTypeDeleteDocument,
      variables: {
        ventureContactTypeId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureContactType",
      id: ventureContactTypeId.toString(),
      fieldNames: ["ventureContactTypesPaginated", "ventureContactTypeList"],
    });

    return Boolean(res?.data?.ventureContactTypeDelete);
  } catch (error) {
    throw error;
  }
}

export async function getVentureContactTypes(
  variables: IVentureContactTypesListVars
): Promise<VentureContactTypesListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureContactTypeListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureContactTypeList;
  } catch (error) {
    throw error;
  }
}
